import { ErrorType, PermissionError, GenericError } from './SocketError';

/**
 * Factory class for creating appropriate error objects based on error type
 */
export class ErrorFactory {
  /**
   * Create appropriate error object based on error response
   * @param errorData The raw error data from API response
   * @returns The appropriate typed error object
   */
  static createError(errorData: any): PermissionError | GenericError {
    if (!errorData || !errorData.type) {
      return this.createGenericError('Unknown error', 'generic');
    }

    switch (errorData.type) {
      case ErrorType.PERMISSION:
        return this.createPermissionError(errorData.error || [], errorData.type);
      case ErrorType.DATABASE:
      case ErrorType.GENERIC:
      default:
        return this.createGenericError(
          Array.isArray(errorData.error) ? errorData.error[0]?.cause : errorData.error,
          errorData.type
        );
    }
  }


  public static isPermissionError(errorData: any): boolean {
    const error = this.createError(errorData);
    return error.type === ErrorType.PERMISSION;
 }

  /**
   * Create a permission error object
   * @param error Array of error items
   * @param type Error type
   * @returns PermissionError object
   */
  private static createPermissionError(error: any[], type: string): PermissionError {
    return {
      error: Array.isArray(error) ? error : [],
      type: type
    };
  }

  /**
   * Create a generic error object
   * @param error Error message
   * @param type Error type
   * @returns GenericError object
   */
  private static createGenericError(error: any, type: string): GenericError {
    return {
      error: typeof error === 'string' ? error : 'Unknown error occurred',
      type: type
    };
  }

  /**
   * Safely extract error message from various error types
   * @param error The error object (PermissionError or GenericError)
   * @returns A user-friendly error message
   */
  static getErrorMessage(error: PermissionError | GenericError): string {
    if (!error) {
      return 'Unknown error occurred';
    }

    if (error.type === ErrorType.PERMISSION && 'error' in error && Array.isArray(error.error)) {
      return error.error.length > 0 && error.error[0].cause 
        ? error.error[0].field + ' ' + error.error[0].cause 
        : 'Permission error occurred';
    }

    if ('error' in error && typeof error.error === 'string') {
      return error.error || 'Error occurred';
    }

    return 'An unexpected error occurred';
  }
}
